import type { MusicSettings } from '@/types/music';
export const MUSIC_AUDIO_MAX_BYTES = 50 * 1024 * 1024;
export const MUSIC_ARTWORK_MAX_BYTES = 5 * 1024 * 1024;
export const MUSIC_MAX_SECONDS = 60 * 60;
export const MUSIC_DEFAULTS: MusicSettings = { enabled: false, defaultVolume: 0.2, defaultPlaylistId: '', welcomePlaylistId: '', welcomeTrackId: '', playlists: [], revision: 0 };
export const MUSIC_PREFERENCES_KEY = 'untangled-music-v1';
export function musicRouteIncluded(path: string) {
  return /^\/(?:welcome|home|shop|titles|collections|blog|podcast|about|contact|first-chapter|assessment|newsletter|privacy|terms|refund-policy|shipping|faq|music|books|authors|pricing|claim|access|affiliates|affiliate|refer)(?:\/|$)/.test(path) || path === '/';
}
export type MusicPreferences = { volume?: number; muted?: boolean; collapsed?: boolean; trackId?: string; version?: string; position?: number; shuffle?: boolean; repeat?: 'off' | 'all' | 'one'; playlistId?: string };
export function parseMusicPreferences(raw: string | null): MusicPreferences {
  try {
    const value = JSON.parse(raw || '{}'); if (!value || typeof value !== 'object') return {};
    const result: MusicPreferences = {};
    if (typeof value.volume === 'number' && Number.isFinite(value.volume) && value.volume >= 0 && value.volume <= 1) result.volume = value.volume;
    for (const key of ['muted', 'collapsed', 'shuffle'] as const) if (typeof value[key] === 'boolean') result[key] = value[key];
    for (const key of ['trackId', 'playlistId', 'version'] as const) if (typeof value[key] === 'string' && /^[a-zA-Z0-9_-]{0,160}$/.test(value[key])) result[key] = value[key];
    if (Number.isFinite(value.position) && value.position >= 0 && value.position <= MUSIC_MAX_SECONDS) result.position = value.position;
    if (['off', 'all', 'one'].includes(value.repeat)) result.repeat = value.repeat;
    return result;
  } catch { return {}; }
}
export function musicQueueOrder(ids: string[], currentId: string, shuffle: boolean, random = Math.random) {
  if (!shuffle) return [...ids];
  const rest = ids.filter(id => id !== currentId);
  for (let index = rest.length - 1; index > 0; index--) {
    const target = Math.floor(Math.max(0, Math.min(.999999, random())) * (index + 1));
    [rest[index], rest[target]] = [rest[target], rest[index]];
  }
  return ids.includes(currentId) ? [currentId, ...rest] : rest;
}
export function nextMusicIndex(length: number, current: number, direction: 1 | -1, repeat: 'off' | 'all' | 'one', ended = false) {
  if (!length) return -1;
  if (ended && repeat === 'one') return Math.max(0, current);
  const next = current + direction;
  if (next >= 0 && next < length) return next;
  return repeat === 'all' ? (next + length) % length : -1;
}
export function musicRange(header: string | null, size: number): { start: number; end: number } | null {
  if (!header) return null;
  const match = /^bytes=(\d*)-(\d*)$/.exec(header);
  if (!match || (!match[1] && !match[2])) throw new Error('Invalid byte range.');
  const start = match[1] ? Number(match[1]) : Math.max(0, size - Number(match[2]));
  const end = match[1] && match[2] ? Math.min(size - 1, Number(match[2])) : size - 1;
  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 0 || start > end || start >= size) throw new Error('Unsatisfiable byte range.');
  return { start, end };
}
